/**
 * AlgoTriggerSection — declares how the push worker should discover
 * candidates for this algo:
 *   - subscribe: real-time, listens on alpha's notification-candidates PubSub
 *     topic. Available only for algos whose root index is one alpha publishes
 *     to (alpha-notifications-*, *-notifications aliases).
 *   - poll: scheduled ES query against any index. The customer-defined
 *     signals path.
 *
 * The mode is auto-suggested from the algo's root index but the user can
 * override. Persists into the algo's `trigger` JSONB column via
 * POST/PATCH /deploy/algos.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

const SUBSCRIBE_INDEX_PATTERNS = [
  /^alpha-notifications-/,
  /-notifications$/,
  /^notification-/,
];

function isSubscribeCapableIndex(index) {
  if (!index) return false;
  return SUBSCRIBE_INDEX_PATTERNS.some((re) => re.test(index));
}

function extractRootIndex(algorithm) {
  if (typeof algorithm !== 'string') return null;
  const m = algorithm.match(/\.index\(\s*['"]([\w-]+)['"]\s*\)/);
  return m ? m[1] : null;
}

const DEFAULT_SUBSCRIBE = { mode: 'subscribe', topic: 'notification-candidates' };
const DEFAULT_POLL = {
  mode: 'poll',
  interval_seconds: 30,
  cursor_field: 'timestamp',
  dedupe_key: 'signal_id',
  initial_lookback_seconds: 0,
};

export default function AlgoTriggerSection({ algorithm, trigger, onChange }) {
  const detectedIndex = useMemo(() => extractRootIndex(algorithm), [algorithm]);
  const subscribeCapable = isSubscribeCapableIndex(detectedIndex);

  // If parent passed null / undefined, auto-pick a default from the index.
  const effective = trigger ?? (subscribeCapable ? DEFAULT_SUBSCRIBE : DEFAULT_POLL);

  // When the index changes and the current mode is no longer valid, suggest
  // (but don't force) the appropriate default.
  const [hint, setHint] = useState(null);
  useEffect(() => {
    if (!detectedIndex) {
      setHint(null);
      return;
    }
    if (effective.mode === 'subscribe' && !subscribeCapable) {
      setHint(
        `"${detectedIndex}" isn't a PubSub source — switch to poll mode or pick a notifications-* index.`,
      );
    } else if (effective.mode === 'poll' && subscribeCapable) {
      setHint(
        `"${detectedIndex}" has a PubSub stream — subscribe mode gives sub-second latency for this index.`,
      );
    } else {
      setHint(null);
    }
  }, [detectedIndex, subscribeCapable, effective.mode]);

  const update = useCallback(
    (patch) => {
      onChange?.({ ...effective, ...patch });
    },
    [effective, onChange],
  );

  const handleModeChange = useCallback(
    (next) => {
      if (next === 'subscribe') onChange?.(DEFAULT_SUBSCRIBE);
      else onChange?.({ ...DEFAULT_POLL });
    },
    [onChange],
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Divider textAlign="left">
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          Trigger
        </Typography>
      </Divider>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
        <FormControl size="small" fullWidth>
          <InputLabel id="trigger-mode-label">Mode</InputLabel>
          <Select
            labelId="trigger-mode-label"
            label="Mode"
            value={effective.mode}
            onChange={(e) => handleModeChange(e.target.value)}
          >
            <MenuItem value="subscribe">
              Subscribe — real-time, PubSub (sub-second)
            </MenuItem>
            <MenuItem value="poll">
              Poll — scheduled ES query (interval-based)
            </MenuItem>
          </Select>
        </FormControl>

        {hint && (
          <Alert severity="info" sx={{ py: 0 }}>
            {hint}
          </Alert>
        )}

        {effective.mode === 'subscribe' && (
          <Typography variant="caption" color="text.secondary">
            Worker listens on PubSub topic{' '}
            <code>{effective.topic || 'notification-candidates'}</code>. Every
            published signal is evaluated against this algo's filters.
          </Typography>
        )}

        {effective.mode === 'poll' && (
          <>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Poll interval: <strong>{effective.interval_seconds}s</strong>
                {' · '}min 10s
              </Typography>
              <Slider
                size="small"
                value={Number(effective.interval_seconds) || 30}
                min={10}
                max={3600}
                step={null}
                marks={[
                  { value: 10, label: '10s' },
                  { value: 30, label: '30s' },
                  { value: 60, label: '1m' },
                  { value: 300, label: '5m' },
                  { value: 1800, label: '30m' },
                  { value: 3600, label: '1h' },
                ]}
                onChange={(_, v) => update({ interval_seconds: Number(v) })}
              />
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                size="small"
                label="Cursor field"
                value={effective.cursor_field || ''}
                onChange={(e) => update({ cursor_field: e.target.value })}
                helperText="ES doc field to watermark on"
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Dedupe key"
                value={effective.dedupe_key || ''}
                onChange={(e) => update({ dedupe_key: e.target.value })}
                helperText="Unique doc field for the per-day seen-set"
                sx={{ flex: 1 }}
              />
            </Stack>

            <TextField
              size="small"
              type="number"
              label="Initial lookback (seconds)"
              value={effective.initial_lookback_seconds ?? 0}
              onChange={(e) =>
                update({ initial_lookback_seconds: Math.max(0, Number(e.target.value) || 0) })
              }
              inputProps={{ min: 0, max: 7 * 24 * 3600 }}
              helperText="On first run, fetch docs from up to this many seconds ago (0 = only new docs)"
              fullWidth
            />
          </>
        )}
      </Box>
    </Box>
  );
}
